/**
 * Homepage offer preferences (regions & shops).
 *
 * Stored locally on the device (localStorage), following the same pattern as
 * the other NexKey settings in src/lib/settings.tsx. An empty array means
 * "All" — the group applies no restriction. `null` from
 * loadOfferPreferences() means the user has never saved preferences, so the
 * homepage shows the intro dialog once.
 */

export interface OfferPreferences {
  /** Normalized region keys (see normalizeOfferRegion in offer-visuals.ts). Empty = all regions. */
  regions: string[];
  /** Shop slugs. Empty = all shops. */
  shops: string[];
}

export const OFFER_PREFS_STORAGE_KEY = "nexkey_offer_prefs";

/**
 * Region groups offered in the homepage preference dialog. Keys and labels
 * match the multi-country groups produced by normalizeOfferRegion, so a saved
 * preference applies directly to the game-detail region filter keys.
 */
export const PREFERENCE_REGIONS: { key: string; label: string }[] = [
  { key: "global", label: "Global" },
  { key: "europe", label: "Europe" },
  { key: "north-america", label: "North America" },
  { key: "latam", label: "LATAM" },
  { key: "asia", label: "Asia" },
  { key: "cis", label: "CIS" },
  { key: "rest-of-world", label: "Rest of World" },
  { key: "unknown", label: "Unknown" },
];

export function loadOfferPreferences(): OfferPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(OFFER_PREFS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OfferPreferences>;
    return {
      regions: Array.isArray(parsed.regions) ? parsed.regions.filter((v) => typeof v === "string") : [],
      shops: Array.isArray(parsed.shops) ? parsed.shops.filter((v) => typeof v === "string") : [],
    };
  } catch {
    return null;
  }
}

export function saveOfferPreferences(prefs: OfferPreferences) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    OFFER_PREFS_STORAGE_KEY,
    JSON.stringify({ regions: prefs.regions, shops: prefs.shops }),
  );
}

/** Toggle one value in a multi-select group (OR semantics within a group). */
export function togglePreferenceValue(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}
